import { DashboardCell } from '@modules/dashboard/components/Layout/DashboardCell';
import { DashboardCellResizePreview } from '@modules/dashboard/components/Layout/DashboardCellResizePreview';
import { DashboardCellResizer } from '@modules/dashboard/components/Layout/DashboardCellResizer';
import {
  getCellWidth,
  getColWidth,
  RowCellsResizeController,
} from '@modules/dashboard/components/Layout/helpers';
import {
  getDashboardRow,
  getMaxCellsMinHeight,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { DashboardState } from '@modules/dashboard/store/types';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { Box, Stack } from '@mui/material';
import {
  DashboardCell as DashboardCellType,
  DashboardRow,
} from 'generated/graphql';
import { cloneDeep } from 'lodash';
import { MouseEvent, useEffect, useRef } from 'react';
import { useMeasure, useUnmount } from 'react-use';

interface DashboardRowCellsProps {
  row: DashboardRow;
  onResizeEnd: (metadata: DashboardState['metadata']) => void;
  shouldDisableInteraction?: boolean;
}

export function DashboardRowCells({
  row,
  onResizeEnd,
  shouldDisableInteraction = false,
}: DashboardRowCellsProps) {
  const [containerRef, { width }] = useMeasure();
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const rows = useDashboardStore(({ metadata }) => metadata.layout.rows);
  const dragItemData = useDashboardStore(({ visual }) => visual.dragItemData);
  const dropTargetData = useDashboardStore(
    ({ visual }) => visual.dropTargetData,
  );
  const cellMinimumHeights = useDashboardStore(
    ({ visual }) => visual.cellMinimumHeightsByRowID[row.id],
  );

  const resizerRef = useRef(
    new RowCellsResizeController({
      rowWidth: width,
      rowCells: row.cells,
      onResize: (updatedCells: DashboardCellType[]) => {
        requestAnimationFrame(() => {
          const updatedMetaData = cloneDeep(
            useDashboardStore.getState().metadata,
          );
          const clonedRow = getDashboardRow({
            rowID: row.id,
            rows: updatedMetaData.layout.rows,
          });

          if (clonedRow) {
            clonedRow.cells = updatedCells;

            dispatch({
              type: DashboardActionType.SET_METADATA,
              payload: { layout: updatedMetaData.layout },
            });
          }
        });
      },
      onResizeEnd: () => {
        const updatedMetaData = cloneDeep(
          useDashboardStore.getState().metadata,
        );

        trackDashboardEvent({
          eventName: 'Dashboard Widget Resize',
        });

        onResizeEnd(updatedMetaData);
      },
    }),
  );

  useEffect(() => {
    resizerRef.current.setRowWidth(width);
  }, [width]);

  useEffect(() => {
    resizerRef.current.resetRowCells(row.cells);
  }, [row.cells]);

  useUnmount(() => {
    resizerRef.current.disconnect();
  });

  function getDropIndicatorIndex() {
    if (!dragItemData || !dropTargetData) {
      return Number.MAX_VALUE;
    }

    const isDropTargetInTheSameRow = dropTargetData.rowID === row.id;
    if (!isDropTargetInTheSameRow) {
      return Number.MAX_VALUE;
    }

    const isDraggedAndDropTargetSame =
      dragItemData.cellID === dropTargetData.cellID;

    if (isDraggedAndDropTargetSame) {
      return Number.MAX_VALUE;
    }

    const dropTargetRow = getDashboardRow({
      rowID: dropTargetData.rowID,
      rows,
    });

    if (!dropTargetRow) {
      return;
    }

    let dropTargetCellIdx = dropTargetRow.cells.findIndex(
      (item) => item.id === dropTargetData.cellID,
    );

    dropTargetCellIdx =
      dropTargetCellIdx + (dropTargetData.isLeftDrop ? -1 : 0);

    return dropTargetCellIdx;
  }

  const rowHeight = Math.max(
    getMaxCellsMinHeight(cellMinimumHeights),
    row.height,
  );

  const dropIndicatorIndex = getDropIndicatorIndex();

  const { firstVisiblePreviewIndicator, lastVisiblePreviewIndicator } =
    resizerRef.current.getVisiblePreviewIndicators();

  return (
    <Box ref={containerRef}>
      {resizerRef.current.getResizeIndex() > 0 && (
        <DashboardCellResizePreview
          colWidth={getColWidth(width)}
          firstVisiblePreviewIndicator={firstVisiblePreviewIndicator}
          lastVisiblePreviewIndicator={lastVisiblePreviewIndicator}
          sx={{ top: '-10px', position: 'absolute' }}
        />
      )}
      <Stack data-id={`${row.id}`} className="row-cells" direction="row">
        <DashboardCellResizer
          key="zero-cell-drop-indicator"
          rowID={row.id}
          rowHeight={rowHeight}
          skipInteractions={true}
          showDropIndicator={-1 === dropIndicatorIndex}
          sx={{ position: 'absolute', left: '-10px' }}
        />
        {row.cells.map((cell, index) => {
          const cellWidth = getCellWidth({
            colSpan: cell.columnSpan,
            rowCells: row.cells,
            rowWidth: width,
          });

          return (
            <DashboardCell
              key={cell.id}
              id={cell.id}
              row={row}
              rowHeight={rowHeight}
              cell={cell}
              showDropIndicator={index === dropIndicatorIndex}
              isLastCell={row.cells.length - 1 === index}
              sx={{ width: cellWidth }}
              shouldDisableInteraction={shouldDisableInteraction}
              onMouseDown={
                shouldDisableInteraction
                  ? undefined
                  : (event: MouseEvent<HTMLDivElement>) => {
                      resizerRef.current.startResizingCellWidth(
                        event.nativeEvent,
                        index + 1,
                      );
                    }
              }
            />
          );
        })}
      </Stack>
    </Box>
  );
}
